import React from 'react';
import { Button } from './ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Key, Users, Settings, AlertTriangle, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Configuration: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user?.role !== 'super-admin') {
    return (
      <div className='flex justify-center items-center py-16'>
        <Card>
          <CardContent className='text-center py-8'>
            <AlertTriangle className='w-12 h-12 text-yellow-500 mx-auto mb-4' />
            <h2 className='text-xl font-semibold mb-2'>Access Denied</h2>
            <p className='text-gray-600'>
              Only super administrators can access this section.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-4 sm:space-y-6'>
      <div className='flex justify-between items-center'>
        <div>
          <h1 className='text-2xl sm:text-3xl font-bold text-gray-900 flex items-center'>
            <Settings className='w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3' />
            System Configuration
          </h1>
          <p className='text-sm sm:text-base text-gray-600 mt-1'>
            Control panel for system configuration and administration
          </p>
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* Local Users Configuration */}
        <Card className='hover:shadow-lg transition-shadow cursor-pointer'>
          <CardHeader>
            <CardTitle className='flex items-center'>
              <Users className='w-5 h-5 mr-2 text-blue-600' />
              Team Setup
            </CardTitle>
            <CardDescription>
              Management and statistics of users registered in the system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className='text-sm text-gray-600 mb-4'>
              Manage team, review approval statistics and manage permissions.
            </p>
            <Button
              onClick={() => navigate('/admin/configuration/local-users')}
              className='w-full flex items-center justify-center space-x-2'
            >
              <span>Team Setup</span>
              <ArrowRight className='w-4 h-4' />
            </Button>
          </CardContent>
        </Card>

        {/* Webhook API Keys Configuration */}
        <Card className='hover:shadow-lg transition-shadow cursor-pointer'>
          <CardHeader>
            <CardTitle className='flex items-center'>
              <Key className='w-5 h-5 mr-2 text-green-600' />
              Webhook API Keys
            </CardTitle>
            <CardDescription>
              Authentication configuration for external webhooks
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className='text-sm text-gray-600 mb-4'>
              Create, manage and delete API keys for webhook authentication.
            </p>
            <Button
              onClick={() => navigate('/admin/configuration/webhook-api-keys')}
              className='w-full flex items-center justify-center space-x-2'
            >
              <span>Manage API Keys</span>
              <ArrowRight className='w-4 h-4' />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Future configurations can be added here as new cards */}
      <div className='mt-8 p-4 bg-gray-50 rounded-lg'>
        <p className='text-sm text-gray-600 text-center'>
          More configuration options will be available soon
        </p>
      </div>
    </div>
  );
};

export default Configuration;
